import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  alpha,
  Badge,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalOffer as CouponIcon,
  ShoppingBag as ShoppingBagIcon,
  East as EastIcon,
  DeleteSweep as ClearCartIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  LocalShipping as DeliveryIcon,
  Discount as DiscountIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {
  getCartItems,
  updateCartQuantity,
  removeCartItem,
  ClearCart,
  calculateCartTotal,
  DeliveryForZipcode,
  FetchAddress,
} from "../../../services/api/cartService";
import { useSelector } from "react-redux";
import AddressDrawer from "../../../pages/AddressManagement/AddressManagement";
import { CreateOrder } from "../../../services/api/orderService";
// import { PaymentModal } from "../../../pages/razorPay/razorpay";
import {
  loadRazorpayScript,
  recordPayment,
} from "../../../pages/razorPay/razorpay";

// Loading Skeleton Component
const CartSkeleton = () => (
  <Box sx={{ p: 3 }}>
    {[1, 2, 3].map((item) => (
      <Paper key={item} sx={{ p: 2, mb: 2, display: "flex", gap: 2 }}>
        <Box
          sx={{ width: 100, height: 100, bgcolor: "#f0f0f0", borderRadius: 2 }}
        />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              width: "60%",
              height: 20,
              bgcolor: "#f0f0f0",
              mb: 1,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              width: "40%",
              height: 14,
              bgcolor: "#f0f0f0",
              mb: 1,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              width: "30%",
              height: 16,
              bgcolor: "#f0f0f0",
              borderRadius: 1,
            }}
          />
        </Box>
      </Paper>
    ))}
  </Box>
);

const CartDrawer = ({ open, onClose, onCheckout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState([]);
  const [couponError, setCouponError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [clearCartDialog, setClearCartDialog] = useState(false);
  const [clearingCart, setClearingCart] = useState(false);
  const [cartTotalData, setCartTotalData] = useState(null);
  console.log("Cart total", cartTotalData);
  const [calculatingTotal, setCalculatingTotal] = useState(false);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const [deliveryMessage, setDeliveryMessage] = useState("");
  const [addresses, setAddresses] = useState([]);
  const isFetchingTotal = useRef(false);
  const isFetchingAddresses = useRef(false);
  const isFetchingCart = useRef(false);
  const hasFetchedCart = useRef(false);
  const deliveryCheckDone = useRef(false);
  const lastCheckedPostalCode = useRef(null);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const zipcode = useSelector((state) => state.orders?.postalcode);
  const [orderData, setOrderData] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);

  useEffect(() => {
    if (
      open &&
      isAuthenticated &&
      token &&
      !isFetchingCart.current &&
      !hasFetchedCart.current
    ) {
      fetchCartItems();
      fetchUserAddresses();
      hasFetchedCart.current = true;
    }
  }, [open, isAuthenticated, token]);

  useEffect(() => {
    if (
      cartItems.length > 0 &&
      selectedAddressId &&
      !isFetchingTotal.current &&
      !loading
    ) {
      fetchCartTotal();
    }
  }, [cartItems, selectedAddressId, appliedCoupons]);

  const fetchUserAddresses = async () => {
    if (!user?.uuid || isFetchingAddresses.current) return;

    isFetchingAddresses.current = true;
    try {
      const response = await FetchAddress(user.uuid);
      if (response.data?.success && response.data?.data) {
        setAddresses(response.data.data);

        if (!selectedAddressId) {
          const defaultAddr = response.data.data.find((addr) => addr.isDefault);
          const addressToSelect = defaultAddr || response.data.data[0];

          if (addressToSelect) {
            setSelectedAddressId(addressToSelect.id);
            setSelectedAddress(addressToSelect);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      isFetchingAddresses.current = false;
    }
  };

  // Handle address selection from AddressDrawer
  const handleAddressSelect = (addressId) => {
    const address = addresses.find((addr) => addr.id === addressId);
    if (address) {
      setSelectedAddressId(addressId);
      setSelectedAddress(address);
      setDeliveryAvailable(true);
      setDeliveryMessage("");
    }
  };

  // Fetch cart items from API (only called when drawer opens)
  const fetchCartItems = async () => {
    if (isFetchingCart.current) return;

    isFetchingCart.current = true;
    setLoading(true);
    try {
      const response = await getCartItems();

      if (response.data?.success) {
        const transformedItems = transformCartData(response.data.data);
        setCartItems(transformedItems);
      } else {
        showSnackbar(response.data?.message || "Failed to load cart", "error");
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      showSnackbar("Failed to load cart items", "error");
    } finally {
      setLoading(false);
      isFetchingCart.current = false;
    }
  };

  // Fetch cart total from CalculateCartTotal API
  const fetchCartTotal = async () => {
    if (cartItems.length === 0 || isFetchingTotal.current) {
      console.log("Skipping fetchCartTotal - no items or already fetching");
      return;
    }

    if (!selectedAddressId) {
      console.log("Skipping fetchCartTotal - no address selected");
      return;
    }

    isFetchingTotal.current = true;
    setCalculatingTotal(true);

    try {
      const subtotal = cartItems.reduce(
        (sum, item) => sum + item.priceAtTime * item.quantity,
        0,
      );
      const vendorIds = [...new Set(cartItems.map((item) => item.vendorId))];
      const now = new Date();
      const requestedDeliveryTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      const payload = {
        subtotal: subtotal,
        vendorIds: vendorIds,
        zipcode: selectedAddress?.postalCode || zipcode,
        requestedDeliveryTime: requestedDeliveryTime,
        customerAddressId: selectedAddressId,
      };

      if (appliedCoupons.length > 0) {
        payload.couponCodes = [...appliedCoupons.map((coupon) => coupon.code)];
      }

      const response = await calculateCartTotal(payload);
      console.log("CalculateCartTotal Response:", response);

      if (response.data?.success && response.data?.data) {
        setCartTotalData(response.data.data);

        if (
          appliedCoupons.length > 0 &&
          response.data.data.breakdown?.grandTotalDiscount > 0
        ) {
          showSnackbar(
            `Coupon ${appliedCoupons[appliedCoupons.length - 1]?.code} applied successfully!`,
            "success",
          );
        } else if (
          appliedCoupons.length > 0 &&
          response.data.data.breakdown?.grandTotalDiscount === 0
        ) {
          showSnackbar("Coupon code is not valid for this order", "error");
          setAppliedCoupons([]);
          setCouponCode("");
        }
      } else {
        console.error(
          "Failed to calculate cart total:",
          response.data?.message,
        );
        if (appliedCoupons.length > 0) {
          showSnackbar(
            response.data?.message || "Invalid coupon code",
            "error",
          );
          setAppliedCoupons([]);
          setCouponCode("");
        }
        calculateLocalTotals();
      }
    } catch (error) {
      console.error("Error calculating cart total:", error);
      calculateLocalTotals();
    } finally {
      setCalculatingTotal(false);
      isFetchingTotal.current = false;
    }
  };

  // Fallback local calculation
  const calculateLocalTotals = () => {
    console.log("Cart total:", cartItems);
    const subtotal = cartItems.reduce(
      (sum, item) => sum + item.priceAtTime * item.quantity,
      0,
    );
    const deliveryCharge = subtotal >= 500 ? 0 : 40;
    const platformFee = 5;
    const gst = subtotal * 0.05;

    let couponDiscount = 0;
    if (appliedCoupons.length > 0) {
      couponDiscount = subtotal * 0.1;
      if (couponDiscount > 200) couponDiscount = 200;
    }

    const grandTotal =
      subtotal + deliveryCharge + platformFee + gst - couponDiscount;

    setCartTotalData({
      subtotal: subtotal,
      totalCharges: deliveryCharge + platformFee + gst,
      grandTotal: grandTotal,
      charges: [
        {
          id: 1,
          chargeType: "Delivery Charge",
          description: "Charges for delivery services",
          chargeValue: deliveryCharge,
          originalChargeValue: deliveryCharge,
        },
        {
          id: 2,
          chargeType: "Platform Fee",
          description: "Platform service charges",
          chargeValue: platformFee,
          originalChargeValue: platformFee,
        },
        {
          id: 3,
          chargeType: "GST",
          description: "Goods and Services Tax",
          chargeValue: gst,
          originalChargeValue: gst,
        },
      ],
      breakdown: {
        originalSubtotal: subtotal,
        cartLevelDiscount: 0,
        subtotalAfterCartSchemes: subtotal,
        originalTotalCharges: deliveryCharge + platformFee + gst,
        chargeLevelDiscount: 0,
        totalAfterCharges: grandTotal + couponDiscount,
        grandTotalDiscount: couponDiscount,
        finalGrandTotal: grandTotal,
      },
    });
  };

  // Update transformCartData to include all required fields
  const transformCartData = (apiData) => {
    console.log("Transforming cart data:", apiData);
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((item) => ({
      id: item.productId,
      cartItemId: item.cartItemId,
      name: item.productName,
      price: item.priceAtTime,
      priceAtTime: item.priceAtTime,
      originalPrice: item.mrp,
      salePrice: item.salePrice,
      quantity: item.quantity,
      image: item.imageUrl,
      category: item.vendorName,
      inStock: true,
      vendorId: item.vendorId,
      variantId: item.variantId,
      vendorProductId: item.vendorProductId,
      vendorVariantId: item.vendorVariantId,
      hsn_code: item.hsn_code || 30059040,
      specialInstructions: item.specialInstructions,
      discount:
        item.mrp > item.priceAtTime
          ? `${Math.round(((item.mrp - item.priceAtTime) / item.mrp) * 100)}% off`
          : null,
    }));
  };
  // Update quantity
  const updateQuantity = async (cartItemId, newQuantity, variantId = null) => {
    if (newQuantity < 1) return;

    setUpdatingItem(cartItemId);
    try {
      const response = await updateCartQuantity(
        cartItemId,
        newQuantity,
        variantId,
      );

      if (response.data?.success) {
        const updatedItems = cartItems.map((item) =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: newQuantity }
            : item,
        );
        setCartItems(updatedItems);
        showSnackbar("Cart updated successfully", "success");
      } else {
        showSnackbar(
          response.data?.message || "Failed to update quantity",
          "error",
        );
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      showSnackbar("Failed to update quantity", "error");
    } finally {
      setUpdatingItem(null);
    }
  };

  // Remove item from cart
  const removeItem = async (cartItemId, variantId = null) => {
    setUpdatingItem(cartItemId);
    try {
      const response = await removeCartItem(cartItemId, variantId);

      if (response.data?.success) {
        setCartItems((prev) =>
          prev.filter((item) => item.cartItemId !== cartItemId),
        );
        showSnackbar("Item removed from cart", "success");
      } else {
        showSnackbar(
          response.data?.message || "Failed to remove item",
          "error",
        );
      }
    } catch (error) {
      console.error("Error removing item:", error);
      showSnackbar("Failed to remove item", "error");
    } finally {
      setUpdatingItem(null);
    }
  };

  // Clear entire cart
  const handleClearCart = async () => {
    setClearingCart(true);
    try {
      const response = await ClearCart();

      if (response.data?.success) {
        setCartItems([]);
        setCartTotalData(null);
        setAppliedCoupons([]);
        setCouponCode("");
        hasFetchedCart.current = false;
        showSnackbar("Cart cleared successfully", "success");
        setClearCartDialog(false);
      } else {
        showSnackbar(response.data?.message || "Failed to clear cart", "error");
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      showSnackbar("Failed to clear cart", "error");
    } finally {
      setClearingCart(false);
    }
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    const code = couponCode.trim().toUpperCase();

    if (appliedCoupons.some((coupon) => coupon.code === code)) {
      setCouponError("Coupon code already applied");
      return;
    }

    setCouponError("");

    const newCoupon = { code: code, type: "discount" };
    const updatedCoupons = [...appliedCoupons, newCoupon];

    setAppliedCoupons(updatedCoupons);
    setCouponCode("");

    if (cartItems.length > 0 && selectedAddressId) {
      await fetchCartTotalWithCoupons(updatedCoupons);
    }
  };

  // Fetch cart total with specific coupons
  const fetchCartTotalWithCoupons = async (coupons) => {
    if (
      cartItems.length === 0 ||
      isFetchingTotal.current ||
      !selectedAddressId
    ) {
      return;
    }

    isFetchingTotal.current = true;
    setCalculatingTotal(true);

    try {
      const subtotal = cartItems.reduce(
        (sum, item) => sum + item.priceAtTime * item.quantity,
        0,
      );
      const vendorIds = [...new Set(cartItems.map((item) => item.vendorId))];
      const now = new Date();
      const requestedDeliveryTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

      const payload = {
        subtotal: subtotal,
        vendorIds: vendorIds,
        zipcode: selectedAddress?.postalCode || zipcode,
        requestedDeliveryTime: requestedDeliveryTime,
        customerAddressId: selectedAddressId,
      };

      if (coupons.length > 0) {
        payload.couponCodes = coupons.map((coupon) => coupon.code);
      }

      const response = await calculateCartTotal(payload);

      if (response.data?.success && response.data?.data) {
        setCartTotalData(response.data.data);

        if (response.data.data.breakdown?.grandTotalDiscount > 0) {
          showSnackbar(
            `Coupon ${coupons[coupons.length - 1].code} applied successfully!`,
            "success",
          );
        } else {
          showSnackbar("Coupon code is not valid for this order", "error");
          setAppliedCoupons([]);
        }
      } else {
        console.error("Failed to apply coupon:", response.data?.message);
        showSnackbar(
          response.data?.message || "Failed to apply coupon",
          "error",
        );
        setAppliedCoupons([]);
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      showSnackbar("Error applying coupon", "error");
      setAppliedCoupons([]);
    } finally {
      setCalculatingTotal(false);
      isFetchingTotal.current = false;
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = async (couponToRemove) => {
    const updatedCoupons = appliedCoupons.filter(
      (coupon) => coupon.code !== couponToRemove.code,
    );
    setAppliedCoupons(updatedCoupons);
    showSnackbar(`Coupon ${couponToRemove.code} removed`, "info");

    if (cartItems.length > 0 && selectedAddressId) {
      isFetchingTotal.current = true;
      setCalculatingTotal(true);

      try {
        const subtotal = cartItems.reduce(
          (sum, item) => sum + item.priceAtTime * item.quantity,
          0,
        );
        const vendorIds = [...new Set(cartItems.map((item) => item.vendorId))];
        const now = new Date();
        const requestedDeliveryTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

        const payload = {
          subtotal: subtotal,
          vendorIds: vendorIds,
          zipcode: selectedAddress?.postalCode || zipcode,
          requestedDeliveryTime: requestedDeliveryTime,
          customerAddressId: selectedAddressId,
        };

        if (updatedCoupons.length > 0) {
          payload.couponCodes = updatedCoupons.map((coupon) => coupon.code);
        }

        const response = await calculateCartTotal(payload);

        if (response.data?.success && response.data?.data) {
          setCartTotalData(response.data.data);
        } else {
          calculateLocalTotals();
        }
      } catch (error) {
        console.error("Error recalculating after coupon removal:", error);
        calculateLocalTotals();
      } finally {
        setCalculatingTotal(false);
        isFetchingTotal.current = false;
      }
    }
  };

  // Show snackbar message
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }, 3000);
  };
  const openRazorpayDirectly = async ({
    amount,
    orderNumber,
    orderData,
    userDetails,
  }) => {
    const isLoaded = await loadRazorpayScript();

    if (!isLoaded || typeof window.Razorpay !== "function") {
      showSnackbar(
        "Failed to load payment gateway. Please refresh and try again.",
        "error",
      );
      return;
    }

    const options = {
      key: "rzp_test_rhCfeloZpwX3eY",
      amount: Math.round(amount * 100),
      currency: "INR",
      name: "Zippshoppe",
      description: `Payment for order ${orderNumber}`,
      handler: async (response) => {
        console.log("Razorpay payment success:", response);
        await recordPayment({
          orderNumber,
          amount: Math.round(amount * 100),
          response,
          isSuccess: true,
        });
        showSnackbar("Payment successful! Order confirmed.", "success");
        try {
          await ClearCart();
          setCartItems([]);
          setCartTotalData(null);
          setAppliedCoupons([]);
          setCouponCode("");
          hasFetchedCart.current = false;
        } catch (e) {}
        onClose();
        if (onCheckout)
          onCheckout(cartItems, cartTotalData, selectedAddress, orderData);
      },
      prefill: {
        name: userDetails.name || "Customer",
        email: userDetails.email || "customer@example.com",
        contact: userDetails.phone || userDetails.phoneNumber || "9999999999", // ✅ fixed
      },
      theme: { color: "#f6cc4f" },
      modal: {
        ondismiss: async () => {
          await recordPayment({
            orderNumber,
            amount: Math.round(amount * 100),
            response: {},
            isSuccess: false,
            errorDetails: { description: "Cancelled by user" },
          });
          showSnackbar("Payment cancelled", "info");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", async (error) => {
      console.error("Payment failed:", error);
      await recordPayment({
        orderNumber,
        amount: Math.round(amount * 100),
        response: {},
        isSuccess: false,
        errorDetails: error,
      });
      showSnackbar("Payment failed. Please try again.", "error");
    });
    rzp.open();
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      showSnackbar("Your cart is empty", "warning");
      return;
    }
    if (!selectedAddressId) {
      showSnackbar("Please select a delivery address", "warning");
      return;
    }

    setCreatingOrder(true);

    try {
      const now = new Date();
      const promisedDeliveryTime = new Date(
        now.getTime() + 60 * 60 * 1000,
      ).toISOString();

      // Group cart items by vendor
      const vendorOrdersMap = new Map();

      cartItems.forEach((item) => {
        const vendorId = item.vendorId;
        if (!vendorOrdersMap.has(vendorId)) {
          vendorOrdersMap.set(vendorId, {
            vendorId: vendorId,
            itemsSubtotal: 0,
            vendorNotes: "",
            orderItems: [],
          });
        }

        const vendorOrder = vendorOrdersMap.get(vendorId);
        const itemMrp = item.originalPrice || item.priceAtTime;
        const unitPrice = item.priceAtTime;
        const totalPrice = unitPrice * item.quantity;
        const taxableAmount = unitPrice;
        const cgst = unitPrice * 0.06 || 0;
        const sgst = unitPrice * 0.06 || 0;
        const igst = 0;

        vendorOrder.orderItems.push({
          vendorProductId: item.vendorProductId,
          vendorVariantId: item.vendorVariantId,
          quantity: item.quantity,
          mrp: itemMrp,
          vendorSalePrice: unitPrice,
          unitPrice: unitPrice,
          totalPrice: totalPrice,
          specialInstructions: item.specialInstructions || null,
          prescriptions: null,
          offeringType: "product",
          cgst: cgst * item.quantity,
          sgst: sgst * item.quantity,
          igst: igst,
          hsn_code: item.hsn_code || 30059040,
          percentage_cgst: 6,
          percentage_sgst: 6,
          percentage_igst: 0,
          taxable_amount: taxableAmount * item.quantity,
          isMrp: true,
        });

        vendorOrder.itemsSubtotal += totalPrice;
      });

      const vendorOrders = Array.from(vendorOrdersMap.values());

      const charges = [];

      if (cartTotalData?.charges && cartTotalData.charges.length > 0) {
        cartTotalData.charges.forEach((charge, index) => {
          charges.push({
            billChargeId: charge.id || index + 9,
            chargeName: charge.chargeType,
            chargeType: charge.chargeType,
            calculationMethod: getCalculationMethod(charge.chargeType),
            baseAmount: charge.originalChargeValue || charge.chargeValue,
            calculatedAmount: charge.chargeValue,
            chargeDetails: getChargeDetails(charge.chargeType),
          });
        });
      } else {
        const deliveryCharge = cartTotalData?.grandTotal >= 500 ? 0 : 40;
        const platformFee = 5;
        const handlingCharge = 10;

        if (deliveryCharge > 0) {
          charges.push({
            billChargeId: 11,
            chargeName: "Delivery Charge",
            chargeType: "Delivery Charge",
            calculationMethod: "time_base",
            baseAmount: deliveryCharge,
            calculatedAmount: deliveryCharge,
            chargeDetails: { distance: 1.76 },
          });
        }

        charges.push({
          billChargeId: 13,
          chargeName: "Platform Fee",
          chargeType: "Platform Fee",
          calculationMethod: "fixed_amount",
          baseAmount: platformFee,
          calculatedAmount: platformFee,
          chargeDetails: {},
        });

        if (handlingCharge > 0) {
          charges.push({
            billChargeId: 9,
            chargeName: "Handling Charge",
            chargeType: "Handling Charge",
            calculationMethod: "fixed_amount",
            baseAmount: handlingCharge,
            calculatedAmount: handlingCharge,
            chargeDetails: {},
          });
        }
      }

      const orderPayload = {
        customerUUID: user?.uuid,
        order: {
          itemsSubtotal: cartTotalData?.subtotal || 0,
          totalCharges: cartTotalData?.totalCharges || 0,
          totalAmount: cartTotalData?.grandTotal || 0,
          deliveryAddressId: selectedAddressId,
          promisedDeliveryTime: promisedDeliveryTime,
          customerNotes: "Order placed via mobile app",
          deliveryInstructions: selectedAddress?.deliveryInstructions || "",
          isPriority: false,
          isFar: false,
          ageConcent: false,
        },
        vendorOrders: vendorOrders,
        charges: charges,
      };

      // Call CreateOrder API
      const response = await CreateOrder(orderPayload);

      if (response.data?.success) {
        const orderInfo = response.data; //  read from root, not .data.data

        await openRazorpayDirectly({
          amount: cartTotalData?.grandTotal || 0,
          orderNumber: orderInfo.order_number, // "ORD-260528114753"
          orderData: {
            order_id: orderInfo.order_id,
            order_uuid: orderInfo.order_uuid,
            order_number: orderInfo.order_number,
            vendor_orders: orderInfo.vendor_orders,
            deliveryAddressId: selectedAddressId,
          },
          userDetails: {
            name: user?.name || "",
            email: user?.email || "",
            phone: user?.phoneNumber || "",
          },
        });
      } else {
        showSnackbar(
          response.data?.message || "Failed to create order",
          "error",
        );
      }
    } catch (error) {
      console.error("Error creating order:", error);
      showSnackbar("Failed to create order. Please try again.", "error");
    } finally {
      setCreatingOrder(false);
    }
  };

  // Helper function to determine calculation method based on charge type
  const getCalculationMethod = (chargeType) => {
    switch (chargeType?.toLowerCase()) {
      case "delivery charge":
        return "time_base";
      case "platform fee":
        return "fixed_amount";
      case "handling charge":
        return "fixed_amount";
      case "low order value charge":
        return "low_order";
      case "surge charge":
        return "fixed_amount";
      default:
        return "fixed_amount";
    }
  };

  // Helper function to get additional charge details
  const getChargeDetails = (chargeType) => {
    switch (chargeType?.toLowerCase()) {
      case "delivery charge":
        return { distance: 1.76 };
      default:
        return {};
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    // setPaymentModalOpen(false);
    showSnackbar("Payment successful! Order confirmed.", "success");

    // Clear cart after successful order
    setTimeout(async () => {
      try {
        await ClearCart();
        setCartItems([]);
        setCartTotalData(null);
        setAppliedCoupons([]);
        setCouponCode("");
        hasFetchedCart.current = false;
      } catch (error) {
        console.error("Error clearing cart after order:", error);
      }
    }, 1000);

    // Close the cart drawer and trigger any parent callbacks
    onClose();
    if (onCheckout) {
      onCheckout(cartItems, cartTotalData, selectedAddress, orderData);
    }
  };

  // Add this function to handle payment failure
  const handlePaymentFailure = (error) => {
    console.error("Payment failed:", error);
    showSnackbar("Payment failed. Please try again.", "error");
    // setPaymentModalOpen(false);
  };

  // Add this function to handle payment cancellation
  const handlePaymentCancel = () => {
    console.log("Payment cancelled by user");
    showSnackbar("Payment cancelled", "info");
    // setPaymentModalOpen(false);
  };
  // Get charge icon
  const getChargeIcon = (chargeType) => {
    switch (chargeType?.toLowerCase()) {
      case "delivery charge":
        return "";
      case "platform fee":
        return "";
      case "handling charge":
        return "";
      case "gst":
        return "";
      default:
        return "";
    }
  };

  // Get drawer width
  const getDrawerWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "90%";
    return "1250px";
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: getDrawerWidth(),
            maxWidth: "1250px",
            height: "100vh",
            borderRadius: { xs: 0, sm: "24px 0 0 24px" },
            overflow: "hidden",
            bgcolor: "#fafafa",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background:
                "linear-gradient(135deg, #f6cc4f 0%, #ffe8a2 50%, #f6cc4f 100%)",
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, sm: 2 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                onClick={onClose}
                sx={{ display: { xs: "flex", sm: "none" }, color: "#3a2e00" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Badge
                badgeContent={cartItems.length}
                sx={{
                  "& .MuiBadge-badge": {
                    background:
                      "linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 11,
                  },
                }}
              >
                <ShoppingBagIcon sx={{ fontSize: 28, color: "#3a2e00" }} />
              </Badge>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, sm: 20 },
                  color: "#3a2e00",
                }}
              >
                Your Cart ({cartItems.length} items)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {cartItems.length > 0 && (
                <IconButton
                  onClick={() => setClearCartDialog(true)}
                  sx={{
                    bgcolor: "rgba(0,0,0,0.08)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.15)" },
                  }}
                >
                  <ClearCartIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              )}
              <IconButton
                onClick={onClose}
                // onClick={() => onClose({ fetchCartItems })}
                sx={{
                  bgcolor: "rgba(0,0,0,0.08)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.15)" },
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <CloseIcon sx={{ color: "#3a2e00" }} />
              </IconButton>
            </Box>
          </Box>

          {/* Cart Content */}
          {loading ? (
            <CartSkeleton />
          ) : cartItems.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                p: 4,
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: { xs: 120, sm: 140 },
                  height: { xs: 120, sm: 140 },
                  borderRadius: "50%",
                  bgcolor: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <ShoppingBagIcon
                  sx={{ fontSize: { xs: 60, sm: 70 }, color: "#bbb" }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 1, color: "#333" }}
              >
                Your cart is empty
              </Typography>
              <Typography sx={{ color: "#666", mb: 3, fontSize: 14 }}>
                Looks like you haven't added anything to your cart yet
              </Typography>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  background:
                    "linear-gradient(135deg, #f6cc4f 0%, #ffe8a2 100%)",
                  color: "#3a2e00",
                  fontWeight: 700,
                  borderRadius: 50,
                  px: 4,
                  py: 1.2,
                  textTransform: "none",
                }}
              >
                Start Shopping
              </Button>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <Grid container sx={{ height: "100%", flexWrap: "nowrap" }}>
                {/* LEFT COLUMN - Cart Items (70%) */}
                <Grid
                  item
                  xs={12}
                  md={7}
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    borderRight: { md: "1px solid #eee" },
                    pb: 2,
                    flexShrink: 0,
                    flexGrow: 1,
                  }}
                >
                  {/* Address Selection Section */}
                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Paper
                      elevation={0}
                      onClick={() => setAddressDrawerOpen(true)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: alpha("#f6cc4f", 0.1),
                        border: "1px solid rgba(246, 204, 79, 0.3)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: alpha("#f6cc4f", 0.15),
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <LocationIcon sx={{ color: "#f6cc4f" }} />
                          <Box>
                            <Typography
                              sx={{ fontSize: 12, color: "#666", mb: 0.5 }}
                            >
                              Delivery Address
                            </Typography>
                            {selectedAddress ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: { xs: 13, sm: 14 },
                                    fontWeight: 600,
                                    color: "#1a1a1a",
                                  }}
                                >
                                  {selectedAddress.addressLabel
                                    ?.charAt(0)
                                    .toUpperCase() +
                                    selectedAddress.addressLabel?.slice(1) ||
                                    "Address"}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 12, color: "#666" }}
                                >
                                  {selectedAddress.addressLine1?.substring(
                                    0,
                                    50,
                                  )}
                                  {selectedAddress.addressLine1?.length > 50
                                    ? "..."
                                    : ""}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 11, color: "#888" }}
                                >
                                  {selectedAddress.city},{" "}
                                  {selectedAddress.state} -{" "}
                                  {selectedAddress.postalCode}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  color: "#f6cc4f",
                                  fontWeight: 600,
                                }}
                              >
                                Select delivery address
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {deliveryAvailable && selectedAddress ? (
                            <Chip
                              label="Delivery Available"
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: 11,
                                bgcolor: "#4caf50",
                                color: "#fff",
                              }}
                            />
                          ) : (
                            selectedAddress &&
                            !deliveryAvailable && (
                              <Chip
                                label="Not Deliverable"
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: 11,
                                  bgcolor: "#f44336",
                                  color: "#fff",
                                }}
                              />
                            )
                          )}
                          <EditIcon sx={{ fontSize: 18, color: "#999" }} />
                        </Box>
                      </Box>
                    </Paper>

                    {!deliveryAvailable &&
                      selectedAddress &&
                      deliveryMessage && (
                        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                          {deliveryMessage}
                        </Alert>
                      )}
                  </Box>

                  {/* Cart Items List */}
                  <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Stack spacing={2}>
                      {cartItems.map((item) => (
                        <Paper
                          key={item.cartItemId || item.id}
                          elevation={0}
                          sx={{
                            display: "flex",
                            gap: 2,
                            p: 2,
                            borderRadius: 3,
                            bgcolor: "#fff",
                            transition: "all 0.2s ease",
                            opacity: updatingItem === item.cartItemId ? 0.6 : 1,
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            },
                            position: "relative",
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image || "/placeholder-image.jpg"}
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = "/placeholder-image.jpg";
                            }}
                            sx={{
                              width: { xs: 70, sm: 100 },
                              height: { xs: 70, sm: 100 },
                              borderRadius: 2,
                              objectFit: "cover",
                              bgcolor: "#f5f5f5",
                            }}
                          />

                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box sx={{ flex: 1, pr: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 13, sm: 14 },
                                    mb: 0.5,
                                    color: "#1a1a1a",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 11, color: "#888", mb: 1 }}
                                >
                                  {item.category}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  removeItem(item.cartItemId, item.variantId)
                                }
                                disabled={updatingItem === item.cartItemId}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#d32f2f" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 1,
                                mt: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 14, sm: 15 },
                                    color: "#071233",
                                  }}
                                >
                                  ₹
                                  {(item.priceAtTime * item?.quantity).toFixed(
                                    2,
                                  )}
                                </Typography>
                                {item.originalPrice &&
                                  item.originalPrice > item.priceAtTime && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: 11,
                                        color: "#999",
                                        textDecoration: "line-through",
                                        ml: 1,
                                      }}
                                    >
                                      ₹{item.originalPrice.toFixed(2)}
                                    </Typography>
                                  )}
                              </Box>
                              {item.discount && (
                                <Chip
                                  label={item.discount}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: 10,
                                    bgcolor: "#ffebee",
                                    color: "#d32f2f",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 1.5,
                                bgcolor: "#f5f5f5",
                                borderRadius: 3,
                                width: "fit-content",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  updateQuantity(
                                    item.cartItemId,
                                    item.quantity - 1,
                                    item.variantId,
                                  )
                                }
                                disabled={updatingItem === item.cartItemId}
                                sx={{ p: 0.5 }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  minWidth: 30,
                                  textAlign: "center",
                                }}
                              >
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  updateQuantity(
                                    item.cartItemId,
                                    item.quantity + 1,
                                    item.variantId,
                                  )
                                }
                                disabled={updatingItem === item.cartItemId}
                                sx={{ p: 0.5 }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {updatingItem === item.cartItemId && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(255,255,255,0.7)",
                                borderRadius: 3,
                                zIndex: 1,
                              }}
                            >
                              <CircularProgress size={30} />
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </Grid>

                {/* RIGHT COLUMN - Order Summary (30%) */}
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    height: "100%",
                    overflowY: "auto",
                    bgcolor: "#fff",
                    borderTop: { xs: "1px solid #eee", md: "none" },
                    flexShrink: 0,
                    flexGrow: 0,
                    width: { md: "30%" },
                  }}
                >
                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 2, color: "#1a1a1a" }}
                    >
                      Order Summary
                    </Typography>

                    {calculatingTotal ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress size={40} />
                      </Box>
                    ) : cartTotalData ? (
                      <>
                        <Stack spacing={1.5}>
                          {/* ALWAYS SHOW ORIGINAL SUBTOTAL - But cross it out if cartLevelDiscount > 0 */}
                         {/* Only show if cartLevelDiscount exists AND is greater than 0 */}
{cartTotalData?.breakdown?.cartLevelDiscount > 0 && (
  <>
    {/* Original Subtotal */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        borderTop: "1px dashed #e0e0e0",
        pt: 1,
        mt: 0.5,
      }}
    >
      <Typography sx={{ color: "#666", fontSize: 16 }}>
        Original Subtotal
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: 16,
          textDecoration: "line-through",
          color: "#999",
        }}
      >
        ₹
        {cartTotalData?.breakdown?.originalSubtotal?.toFixed(2) ||
          cartTotalData.subtotal?.toFixed(2) ||
          ""}
      </Typography>
    </Box>

    {/* Cart Discount */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Typography sx={{ color: "#2e7d32",fontSize:15 ,fontWeight: 600}}>
        Cart Discount
      </Typography>
      <Typography
        sx={{ color: "#2e7d32", fontWeight: 600,fontSize:15 }}
      >
        -₹
        {cartTotalData.breakdown.cartLevelDiscount.toFixed(2)}
      </Typography>
    </Box>
  </>
)}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography sx={{ color: "#666" }}>
                              Subtotal
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                             ₹{cartTotalData.subtotal?.toFixed(2) || 0}
                            </Typography>
                          </Box>

                          {cartTotalData.charges &&
                            cartTotalData.charges.map((charge) => (
                              <Box
                                key={charge.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography sx={{ fontSize: 16 }}>
                                    {getChargeIcon(charge.chargeType)}
                                  </Typography>
                                  <Typography sx={{ color: "#666" }}>
                                    {charge.chargeType}
                                  </Typography>
                                </Box>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    color:
                                      charge.chargeValue === 0
                                        ? "#2e7d32"
                                        : "#666",
                                  }}
                                >
                                  {charge.chargeValue === 0
                                    ? "FREE"
                                    : `₹${charge.chargeValue.toFixed(2)}`}
                                </Typography>
                              </Box>
                            ))}

                          {cartTotalData.breakdown?.grandTotalDiscount > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography sx={{ color: "#2e7d32" }}>
                                Coupon Discount
                              </Typography>
                              <Typography
                                sx={{ color: "#2e7d32", fontWeight: 600 }}
                              >
                                -₹
                                {cartTotalData.breakdown.grandTotalDiscount.toFixed(
                                  2,
                                )}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ my: 1 }} />

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                              Grand Total
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: 24,
                                color: "#071233",
                              }}
                            >
                              ₹{cartTotalData.grandTotal?.toFixed(2) || 0}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#999",
                              textAlign: "right",
                            }}
                          >
                            Inclusive of all taxes
                          </Typography>
                        </Stack>

                        {/* Coupon Section */}
                        <Box sx={{ mt: 3 }}>
                          {appliedCoupons.length > 0 && (
                            <Stack spacing={1} sx={{ mb: 2 }}>
                              {appliedCoupons.map((coupon, index) => (
                                <Paper
                                  key={index}
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    bgcolor: alpha("#2e7d32", 0.1),
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <DiscountIcon
                                      sx={{ color: "#2e7d32", fontSize: 20 }}
                                    />
                                    <Typography
                                      sx={{
                                        fontWeight: 600,
                                        color: "#2e7d32",
                                        fontSize: 13,
                                      }}
                                    >
                                      {coupon.code}
                                    </Typography>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveCoupon(coupon)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Paper>
                              ))}
                            </Stack>
                          )}

                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Apply coupon code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            error={!!couponError}
                            helperText={couponError}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CouponIcon sx={{ color: "#f6cc4f" }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Button
                                    size="small"
                                    onClick={handleApplyCoupon}
                                    disabled={!couponCode.trim()}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Apply
                                  </Button>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                bgcolor: "#f5f5f5",
                              },
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography sx={{ color: "#666" }}>
                          {!selectedAddressId
                            ? "Please select an address to see total"
                            : "Calculating total..."}
                        </Typography>
                      </Box>
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleCheckout}
                      disabled={
                        loading ||
                        cartItems.length === 0 ||
                        calculatingTotal ||
                        !selectedAddressId ||
                        creatingOrder
                      }
                      endIcon={!creatingOrder && <EastIcon />}
                      sx={{
                        mt: 3,
                        background:
                          selectedAddressId && !creatingOrder
                            ? "linear-gradient(135deg, #f6cc4f 0%, #ffe8a2 50%, #f6cc4f 100%)"
                            : "#ccc",
                        color:
                          selectedAddressId && !creatingOrder
                            ? "#3a2e00"
                            : "#999",
                        fontWeight: 800,
                        fontSize: 16,
                        borderRadius: 50,
                        py: 1.5,
                        textTransform: "none",
                      }}
                    >
                      {creatingOrder ? (
                        <CircularProgress size={24} sx={{ color: "#3a2e00" }} />
                      ) : !selectedAddressId ? (
                        "Select Delivery Address"
                      ) : (
                        "Proceed to Checkout"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={clearCartDialog}
        onClose={() => !clearingCart && setClearCartDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ClearCartIcon sx={{ color: "#d32f2f" }} />
          Clear Cart
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Are you sure you want to clear all items from your cart? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setClearCartDialog(false)}
            disabled={clearingCart}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearCart}
            disabled={clearingCart}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#d32f2f",
            }}
          >
            {clearingCart ? <CircularProgress size={24} /> : "Clear Cart"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Drawer */}
      <AddressDrawer
        open={addressDrawerOpen}
        onClose={() => setAddressDrawerOpen(false)}
        onAddressSelect={handleAddressSelect}
        selectedAddressId={selectedAddressId}
        userId={user?.uuid}
        zipcode={zipcode}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CartDrawer;
